/**
 * Scrambl Query Language (ScQL) — a JQL-style query language for finding
 * records across modules.
 *
 * Example queries:
 *   status = "In Progress" AND assignee = currentUser()
 *   priority IN (HIGH, URGENT) AND due <= endOfWeek() ORDER BY due ASC
 *   project = "Website Redesign" AND (type = Bug OR text ~ "payment")
 *
 * The tokenizer/parser live in @scrambl/shared so both the web app
 * (validation + autocomplete) and the API (translation to Prisma) share one
 * grammar. Module-specific field registries describe what can be queried.
 */

export interface QueryPosition {
  /** Zero-based character offset of the first character */
  start: number
  /** Zero-based character offset AFTER the last character */
  end: number
}

export type QueryComparisonOperator =
  | '='
  | '!='
  | '>'
  | '>='
  | '<'
  | '<='
  | '~'
  | '!~'
  | 'IN'
  | 'NOT IN'
  | 'IS EMPTY'
  | 'IS NOT EMPTY'

export type QueryValueNode =
  | { kind: 'string'; value: string; position: QueryPosition }
  | { kind: 'number'; value: number; position: QueryPosition }
  | { kind: 'boolean'; value: boolean; position: QueryPosition }
  | {
      kind: 'function'
      name: string
      args: QueryValueNode[]
      position: QueryPosition
    }
  /** Relative duration like -7d, 2w, 1M (minutes m, hours h, days d, weeks w, months M, years y) */
  | { kind: 'duration'; raw: string; position: QueryPosition }

export interface QueryComparisonNode {
  kind: 'comparison'
  field: string
  operator: QueryComparisonOperator
  values: QueryValueNode[]
  position: QueryPosition
}

export type QueryExpressionNode =
  | { kind: 'and'; children: QueryExpressionNode[] }
  | { kind: 'or'; children: QueryExpressionNode[] }
  | { kind: 'not'; child: QueryExpressionNode }
  | QueryComparisonNode

export interface QueryOrderByItem {
  field: string
  direction: 'asc' | 'desc'
}

export interface ParsedQuery {
  /** null means "match everything" (query was empty or ORDER BY only) */
  where: QueryExpressionNode | null
  orderBy: QueryOrderByItem[]
}

export class QueryParseError extends Error {
  readonly position: QueryPosition

  constructor(message: string, position: QueryPosition) {
    super(message)
    this.name = 'QueryParseError'
    this.position = position
  }
}

// ---------------------------------------------------------------------------
// Field registry types — each module declares its queryable fields
// ---------------------------------------------------------------------------

export type QueryFieldType =
  | 'text'
  | 'enum'
  | 'user'
  | 'date'
  | 'number'
  | 'boolean'
  | 'reference'

/**
 * Hint telling the query UI where to source value suggestions for a field.
 * Pages supply the actual options (members, projects, statuses, ...).
 */
export type QueryFieldOptionSource =
  | 'statuses'
  | 'members'
  | 'projects'
  | 'entities'
  | 'sprints'
  | 'groups'
  | 'taskTypes'

export interface QueryFieldOption {
  value: string
  label: string
  /**
   * Spellings accepted as this value (case-insensitive) and canonicalized
   * before translation — e.g. CLOSED_WON for WON. Lets a strict enum stay
   * strict without rejecting the slips a model reliably makes (E4).
   */
  aliases?: string[]
}

export interface QueryFieldDefinition {
  /** Canonical field key used in queries, e.g. 'status' */
  key: string
  /** Alternate spellings accepted by the parser/translator */
  aliases?: string[]
  label: string
  type: QueryFieldType
  description?: string
  example?: string
  /** Operators this field supports */
  operators: QueryComparisonOperator[]
  /** Whether the field can appear in ORDER BY */
  sortable?: boolean
  /** Static value suggestions (enum fields) */
  enumValues?: QueryFieldOption[]
  /**
   * When true, `enumValues` is the COMPLETE value set and a value outside it
   * is a validation error (client + server) rather than a silent no-match.
   * Opt-in because some registries list only the common values. Seen live:
   * `stageType NOT IN (CLOSED_WON, CLOSED_LOST)` matched every deal (E4).
   */
  strictEnumValues?: boolean
  /** Dynamic value suggestions source (resolved by the host page/UI) */
  optionSource?: QueryFieldOptionSource
  /** User fields accept currentUser() */
  supportsCurrentUser?: boolean
}

export const QUERY_DATE_FUNCTIONS = [
  'now',
  'startOfDay',
  'endOfDay',
  'startOfWeek',
  'endOfWeek',
  'startOfMonth',
  'endOfMonth',
  'startOfYear',
  'endOfYear',
] as const

export type QueryDateFunctionName = (typeof QUERY_DATE_FUNCTIONS)[number]

export const QUERY_USER_FUNCTIONS = ['currentUser'] as const

export const TEXT_OPERATORS: QueryComparisonOperator[] = [
  '=',
  '!=',
  '~',
  '!~',
  'IN',
  'NOT IN',
  'IS EMPTY',
  'IS NOT EMPTY',
]

export const ENUM_OPERATORS: QueryComparisonOperator[] = [
  '=',
  '!=',
  'IN',
  'NOT IN',
  'IS EMPTY',
  'IS NOT EMPTY',
]

export const DATE_OPERATORS: QueryComparisonOperator[] = [
  '=',
  '!=',
  '>',
  '>=',
  '<',
  '<=',
  'IS EMPTY',
  'IS NOT EMPTY',
]

export const NUMBER_OPERATORS: QueryComparisonOperator[] = [
  '=',
  '!=',
  '>',
  '>=',
  '<',
  '<=',
  'IN',
  'NOT IN',
  'IS EMPTY',
  'IS NOT EMPTY',
]

export const BOOLEAN_OPERATORS: QueryComparisonOperator[] = ['=', '!=']
